import {
    Button,
    HorizontalContainer,
    Modal,
    VerticalContainer,
} from '@/components';
import {
    R,
    format,
} from '@/features/programs/widgets/newborn/resources/NewbornResources.resx';
import {
    AddChildrenModalOptions,
    AddChildrenMode,
    useAddChildren,
} from '@/features/shared/features/addOrEditChild/lib';
import { HouseholdMemberType } from '@/shared/enums';
import { useEffect, useState } from 'react';
import { ChildModal } from '@/features/shared/features/addOrEditChild/ui';
import { subMonths } from 'date-fns';
import { PatientHouseholdMemberDto } from '@/shared/interfaces';
import { BirthdayCelebrationIcon, TickIcon } from '@/components/Icons';
import { useProfile } from '@/features/shared/lib/useProfile';
import { Utils } from '@/shared/Utils';

export type CongratulationOnYourChildModalProps = {
    onClose: () => void;
};

enum Steps {
    Congratulations = 1,
    AddChildCprLookup = 2,
    AddChildManually = 3,
    ChildSuccessfullyAdded = 4,
}

const twelveMonthsAgo = subMonths(new Date(), 12);

export const CongratulationOnYourChildModal = ({
    onClose,
}: CongratulationOnYourChildModalProps) => {
    const { refetch } = useProfile();

    const options: AddChildrenModalOptions = {
        allowedTypes: [HouseholdMemberType.Child],
        mode: AddChildrenMode.Newborn,
        onSubmitAdolescent: null,
        onChildAdded: async () => {
            const updatedProfile = await refetch();
            if (updatedProfile?.error) return;

            if (updatedProfile?.data) {
                const sortedChildren = updatedProfile.data.Kids.filter(
                    (child) => child.DateOfBirth,
                ).sort(
                    (a, b) =>
                        new Date(b.DateOfBirth!).getTime() -
                        new Date(a.DateOfBirth!).getTime(),
                );
                if (!sortedChildren.length) return;
                setChildAddedName(
                    sortedChildren[0].User.FullName.split(' ')[0],
                );
            }
            setStep(Steps.ChildSuccessfullyAdded);
        },
        onClose: onClose,
    };

    const {
        children,
        addChild,
        getChildIcon,
        onMutationSuccess,
        setSelectedChild,
        prefilledChild,
    } = useAddChildren(options);

    const [step, setStep] = useState<Steps>(Steps.Congratulations);
    const [childAddedName, setChildAddedName] = useState<string>('');
    const [newborn, setNewborn] = useState<PatientHouseholdMemberDto | null>(
        null,
    );

    useEffect(() => {
        const unassociatedNewborn = children.find(
            (child) =>
                child.DateOfBirth &&
                new Date(child.DateOfBirth) > twelveMonthsAgo,
        );
        setNewborn(unassociatedNewborn);
    }, [children]);

    const navigateForward = () => {
        if (step === Steps.Congratulations) {
            setStep(newborn ? Steps.AddChildCprLookup : Steps.AddChildManually);
        }
    };

    const handleChildClick = (child: PatientHouseholdMemberDto) => () => {
        setSelectedChild(child);
        setStep(Steps.AddChildManually);
    };

    const title =
        step === Steps.Congratulations
            ? R.CongratulationOnYourChildTitle
            : R.AddChild;

    return (
        <Modal title={title} onClose={onClose}>
            <VerticalContainer spacing="md" className="mt-6!">
                {step === Steps.Congratulations && (
                    <VerticalContainer spacing="none">
                        <p className="text-text-primary mt-0 mb-0">
                            {R.CongratulationOnYourChildParagraph1}
                        </p>

                        <p className="text-text-heading mt-4 mb-0 font-semibold">
                            {R.CongratulationOnYourChildParagraph2Title}
                        </p>
                        <p className="text-text-primary mt-1 mb-0">
                            {R.CongratulationOnYourChildParagraph2}
                        </p>

                        <p className="text-text-heading mt-4 mb-0 font-semibold">
                            {R.CongratulationOnYourChildParagraph3Title}
                        </p>
                        <p className="text-text-primary mt-1 mb-0">
                            {R.CongratulationOnYourChildParagraph3}
                        </p>

                        <div className="mt-4 flex self-end">
                            <Button onClick={navigateForward}>
                                {
                                    R.CongratulationOnYourChildRegisterChildButtonText
                                }
                            </Button>
                        </div>
                    </VerticalContainer>
                )}

                {step === Steps.AddChildCprLookup && newborn && (
                    <>
                        <HorizontalContainer
                            key={newborn.CprNumber}
                            className="bg-bg-surface border-border-attention min-h-[74px] w-full items-center rounded-2xl border-2 p-4 transition-colors"
                        >
                            {getChildIcon(newborn)}
                            <HorizontalContainer className="w-full items-center justify-between">
                                <VerticalContainer spacing="none">
                                    <h5>{newborn.FullName}</h5>
                                    <span className="text-text-secondary my-0 text-sm">
                                        {R.FetchedViaCpr} (
                                        {Utils.formatCpr(newborn.CprNumber)})
                                    </span>
                                </VerticalContainer>

                                <TickIcon className="size-6" />
                            </HorizontalContainer>
                        </HorizontalContainer>

                        <div className="mt-4 flex self-end">
                            <Button onClick={handleChildClick(newborn)}>
                                {R.Add}
                            </Button>
                        </div>
                    </>
                )}

                {step === Steps.AddChildManually && (
                    <ChildModal
                        onClose={onClose}
                        skipCloseAfterSuccess={true}
                        onMutationSuccess={onMutationSuccess}
                        mode={{ isEditing: false, isAdding: true }}
                        prefilledChild={prefilledChild}
                        canAlwaysEditName={true}
                    />
                )}

                {step === Steps.ChildSuccessfullyAdded && (
                    <Modal onClose={onClose}>
                        <VerticalContainer spacing="md">
                            <BirthdayCelebrationIcon className="-mt-10 mb-2 w-full flex-1" />
                            <h2>
                                {R.ChildSuccessfullyAddedTitle},{' '}
                                {childAddedName}
                            </h2>

                            <p className="text-text-primary mt-0 mb-0">
                                {R.ChildWasSuccesfullyAddedParagraph}
                            </p>

                            <div className="mt-4 flex self-end">
                                <Button onClick={onClose}>{R.Close}</Button>
                            </div>
                        </VerticalContainer>
                    </Modal>
                )}

                {addChild.status === 'error' && (
                    <div>{addChild.errorMessage}</div>
                )}
            </VerticalContainer>
        </Modal>
    );
};
